import { useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import { usePageMeta } from '../usePageMeta';
import { StudioLink } from '../components/StudioLink';

// Mirrors index.html's baseline <title>/description -- kept here explicitly,
// rather than just relying on that baseline never being overwritten, so this
// stays correct even if a later page's default drifts from index.html.
const PAGE_TITLE = "Embroidery Online – Convert Text to PES & Embroidery Files Online";
// Kept under ~160 characters -- Google truncates meta descriptions past
// that on desktop, and the previous copy (214 chars) was cut off mid
// sentence in search results instead of at a point that reads cleanly.
const PAGE_DESCRIPTION =
  "Convert text into embroidery files free online. Choose from 100+ fonts, preview stitches, resize, and export PES, DST, JEF, or EXP for Brother, Janome, and more.";

interface HomePageProps {
  onNavigateToCreate: () => void;
  onNavigateToConvert: () => void;
  onNavigateToPreview: () => void;
  onNavigateToDigitize: () => void;
  onNavigateToDigitizingService: () => void;
  formatConverterEnabled: boolean;
  stitchReplayerEnabled: boolean;
  embroideryViewerEnabled: boolean;
  svgDigitizerEnabled: boolean;
  // The digitizing_service flag: the card for the custom (human) digitizing
  // service's quote form.
  digitizingServiceEnabled: boolean;
  // "24 hours", from config, or null to leave the promise off.
  digitizingTurnaround: string | null;
}

interface FAQItem {
  question: string;
  answer: string;
}

const faqData: FAQItem[] = [
  {
    question: "What is an embroidery text generator?",
    answer: "An embroidery text generator converts typed text into stitch-based embroidery files that can be loaded directly into embroidery machines."
  },
  {
    question: "Do I need embroidery software?",
    answer: "No. Embroidery Online runs entirely in your browser and does not require Ink/Stitch, Hatch, Wilcom, or any other embroidery software."
  },
  {
    question: "Can I resize embroidery text?",
    answer: "Yes. You can control the size of your embroidery design and cap the maximum width to fit common hoop sizes."
  },
  {
    question: "Which machines are supported?",
    answer: "Files are compatible with Brother embroidery machines and other major brands that support PES, DST, JEF, or EXP formats."
  },
  {
    question: "What file formats can I export?",
    answer: "You can export your designs to PES, DST, JEF, and EXP formats, ensuring compatibility with most major embroidery machine brands."
  },
  {
    question: "Is there a cost to use this tool?",
    answer: "No. Embroidery Online is free to use -- create and export as many designs as you like."
  },
  {
    question: "Is this a free PES file maker?",
    answer: "Yes. Type your text, pick a font, and it makes a PES file you can download for free, with DST, JEF, and EXP versions included."
  }
];

// Module-level, not inline in JSX -- usePageMeta needs a stable reference
// across renders (see its structuredData doc comment), and this is the
// same reasoning PAGE_TITLE/PAGE_DESCRIPTION above are already constants
// rather than computed in the component body.
//
// FAQPage matches faqData above rather than being hand-duplicated: Google
// only credits FAQ structured data if it matches text actually visible on
// the page. isAccessibleForFree on the WebApplication entry matches
// NoopBilling (app/services/billing.py) -- there is no paid tier to
// describe.
const STRUCTURED_DATA: object[] = [
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqData.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  },
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Embroidery Online",
    url: "https://embroiderytext.com/",
    description: PAGE_DESCRIPTION,
    applicationCategory: "DesignApplication",
    operatingSystem: "Any (runs in a web browser)",
    isAccessibleForFree: true,
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
];

export function HomePage({
  onNavigateToCreate,
  onNavigateToConvert,
  onNavigateToPreview,
  onNavigateToDigitize,
  onNavigateToDigitizingService,
  formatConverterEnabled,
  stitchReplayerEnabled,
  embroideryViewerEnabled,
  svgDigitizerEnabled,
  digitizingServiceEnabled,
  digitizingTurnaround,
}: HomePageProps) {
  usePageMeta({ title: PAGE_TITLE, description: PAGE_DESCRIPTION, path: "/", structuredData: STRUCTURED_DATA });

  const posthog = usePostHog();
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <section className="landing-page">
      <div className="hero-content">
        <h1>Convert Text to Professional Embroidery Files</h1>
        <p className="hero-subtitle">
          A free PES file maker for text: create embroidery-ready designs in seconds. Choose from 100+ embroidery fonts,
          preview stitches, and download PES files compatible with Brother, Janome, Bernina, and many other machines.
          No software installation required.
        </p>
        <div className="hero-actions">
          <button
            className="cta-button"
            onClick={onNavigateToCreate}
          >
            Start Creating →
          </button>
          <StudioLink source="home_hero" className="cta-button cta-button-studio studio-upsell">
            Try Stitch Studio →
          </StudioLink>
        </div>
        {/* What Studio does, under the button that goes there: the text tool
            needs no explaining on a page about text, Studio does. */}
        <div className="hero-studio studio-upsell">
          <p className="hero-studio-title">
            <strong>Stitch Studio</strong>, our full embroidery editor, free for now:
          </p>
          <ul className="hero-studio-features">
            <li>Import SVG artwork and digitize it</li>
            <li>Draw shapes with pen, arc and polygon tools</li>
            <li>Add text in 100+ embroidery fonts</li>
            <li>Fills, satin outlines, textures and skews</li>
            <li>Open PES or DST files and edit single stitches</li>
            <li>Thread colour library and stitch worksheets</li>
            <li>Reorder for fewer colour changes</li>
            <li>Export PES, DST, JEF, EXP and VP3</li>
          </ul>
        </div>
      </div>

      <section aria-label="Tools">
        <div className="tools-grid">
          {/* First and full width: Studio is where the site is headed. */}
          <StudioLink source="home_card" className="tool-card tool-card-service tool-card-featured studio-upsell">
            <h3>🎨 Stitch Studio <span className="nav-badge">Beta</span></h3>
            <p>
              A full embroidery editor in your browser. Digitize SVG artwork, draw shapes, add text, fills, outlines and
              textures, open your existing PES or DST files, and export for your machine.
            </p>
            <span className="tool-card-cta">Open Stitch Studio →</span>
          </StudioLink>
          <a
            className="tool-card"
            href="/create"
            onClick={(e) => { e.preventDefault(); onNavigateToCreate(); }}
          >
            <h3>✏️ Text to Embroidery</h3>
            <p>Type text, choose a font, and export a stitch-ready design.</p>
          </a>
          {formatConverterEnabled && (
            <a
              className="tool-card"
              href="/convert"
              onClick={(e) => { e.preventDefault(); onNavigateToConvert(); }}
            >
              <h3>🔄 Convert Files</h3>
              <p>Convert between PES, DST, JEF, EXP, and VP3, free in your browser.</p>
            </a>
          )}
          {embroideryViewerEnabled && (
            <a
              className="tool-card"
              href="/preview"
              onClick={(e) => { e.preventDefault(); onNavigateToPreview(); }}
            >
              <h3>🔍 Embroidery Viewer</h3>
              <p>Preview a PES, DST, JEF, EXP, or VP3 file instantly, free.</p>
            </a>
          )}
          {stitchReplayerEnabled && (
            <a className="tool-card" href="/replay">
              <h3>▶ Stitch Simulator</h3>
              <p>Upload a design and preview it sewing, stitch by stitch.</p>
            </a>
          )}
          {/* Through navigateTo, not a plain link like the simulator's --
              /digitize needs a token, and navigateTo('digitize') is what
              calls ensureToken() on the way in. */}
          {svgDigitizerEnabled && (
            <a
              className="tool-card"
              href="/digitize"
              onClick={(e) => { e.preventDefault(); onNavigateToDigitize(); }}
            >
              <h3>🖌️ Digitize SVG</h3>
              <p>Turn an SVG into a stitch-ready PES, DST, JEF, or EXP file.</p>
            </a>
          )}
          {digitizingServiceEnabled && (
            <a
              className="tool-card tool-card-service"
              href="/digitizing-service?from=home"
              onClick={(e) => {
                e.preventDefault();
                posthog?.capture("digitizing_home_cta_clicked");
                onNavigateToDigitizingService();
              }}
            >
              <h3>🧵 Custom Digitizing</h3>
              <p>
                Send us your logo or image and we'll digitize it by hand for your machine.
                {digitizingTurnaround && ` Quote within ${digitizingTurnaround}.`}
              </p>
              <span className="tool-card-cta">Get a quote →</span>
            </a>
          )}
        </div>
        {embroideryViewerEnabled && (
          <p className="tools-more">
            <a href="/preview" onClick={(e) => { e.preventDefault(); onNavigateToPreview(); }}>
              Open an embroidery file online →
            </a>
          </p>
        )}
      </section>

      <section className="how-it-works">
        <h2>What is Embroidery Online?</h2>
        <p>
          Embroidery Online is an online embroidery text generator designed for simple,
          high-quality text embroidery. Whether you're creating names, monograms,
          labels, or custom lettering, our tool converts text into machine-ready
          embroidery files without complex design software.
        </p>
        <p>
          It also works as a PES file creator: every design comes out as a PES
          file for Brother machines, generated straight from the text you type,
          so there's no separate PES file generator to install or learn.
        </p>
      </section>

      <div className="how-it-works">
        <h2>How to Make a PES File</h2>
        <div className="steps">
          <div className="step">
            <div className="step-number">1</div>
            <h4>Enter Your Text</h4>
            <p>Type the name, monogram, or lettering you want to embroider</p>
          </div>
          <div className="step">
            <div className="step-number">2</div>
            <h4>Choose a Font</h4>
            <p>Select from our library of embroidery fonts</p>
          </div>
          <div className="step">
            <div className="step-number">3</div>
            <h4>Preview & Download</h4>
            <p>Check the stitches, then download your PES file (or DST, JEF, EXP)</p>
          </div>
        </div>
      </div>

      <div className="features-grid">
        <div className="feature-card">
          <h3>🎨 100+ Fonts</h3>
          <p>Choose from professional embroidery typefaces designed for quality stitching</p>
        </div>
        <div className="feature-card">
          <h3>📁 Multiple Formats</h3>
          <p>Export to PES, DST, EXP, JEF - compatible with all major machines</p>
        </div>
        <div className="feature-card">
          <h3>⚡ Instant Preview</h3>
          <p>See exactly how your design will look before exporting</p>
        </div>
        <div className="feature-card">
          <h3>📏 Custom Sizing</h3>
          <p>Control the exact dimensions of your embroidery pattern</p>
        </div>
      </div>

      <section className="how-it-works-left">
        <h2>Supported Embroidery File Formats & Machines</h2>
        <p>
          Embroidery Online generates embroidery files compatible with popular embroidery
          machines. PES files work with Brother embroidery machines, while additional
          formats support other major brands.
        </p>
        <ul>
          <li>PES – Brother embroidery machines</li>
          <li>DST – Tajima and commercial machines</li>
          <li>JEF – Janome embroidery machines</li>
          <li>EXP – Bernina embroidery machines</li>
        </ul>
      </section>

      <section className="faq-section">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-container">
          {faqData.map((faq, index) => (
            <div key={index} className="faq-item">
              <button
                className="faq-question"
                onClick={() => toggleFAQ(index)}
                aria-expanded={openFAQ === index}
              >
                <span>{faq.question}</span>
                <span className={`faq-icon ${openFAQ === index ? 'open' : ''}`}>
                  {openFAQ === index ? '−' : '+'}
                </span>
              </button>
              <div className={`faq-answer ${openFAQ === index ? 'open' : ''}`}>
                <p>{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </section>
  );
}
